import React, {FC, ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Button,
    Code,
    Collapse,
    Flex,
    FormControl,
    SimpleGrid,
    FormHelperText,
    FormLabel,
    HStack,
    Input,
    Stack,
    Text,
    Textarea,
    VStack,
} from "@chakra-ui/react";
import {RichPlainTextarea} from "../RichPlainTextarea/RichPlainTextarea";
import {useTextHistory} from "../../hooks/useTextHistory";
import {insertTextAtSelection} from "../../shared/utils/inactiveOrderReminderMailPlainText";
import {EmailPreviewShadow} from "./EmailPreviewShadow";

const labelProps = {fontSize: "xs", fontWeight: "medium" as const, mb: 0.5};
const helperProps = {fontSize: "xs", mt: 0.5, mb: 0, lineHeight: "short" as const};
const fieldProps = {size: "sm" as const, fontSize: "sm"};


export type MailTemplatePreviewContext = {
    subject: string;
    textBefore: string;
    textAfter: string;
    previewDealershipName: string;
    emailHtml: string;
    useCustomHtml: boolean;
    htmlEditorOpen: boolean;
};

export type MailTemplateEditorProps = {
    subject: string;
    textBefore: string;
    textAfter: string;
    previewDealershipName: string;
    emailHtml: string;
    useCustomHtml: boolean;
    onSubjectChange: (value: string) => void;
    onTextBeforeChange: (value: string) => void;
    onTextAfterChange: (value: string) => void;
    onPreviewDealershipNameChange: (value: string) => void;
    onEmailHtmlChange: (value: string) => void;
    onUseCustomHtmlChange: (value: boolean) => void;
    buildPreview: (ctx: MailTemplatePreviewContext) => {previewSubject: string; previewHtml: string};
    buildComposedHtml: (textBefore: string, textAfter: string) => string;
    subjectPlaceholder: string;
    textBeforePlaceholder: string;
    textAfterPlaceholder: string;
    dealershipTag: string;
    htmlEditorFooter?: ReactNode;
};

export const MailTemplateEditor: FC<MailTemplateEditorProps> = ({
    subject,
    textBefore,
    textAfter,
    previewDealershipName,
    emailHtml,
    useCustomHtml,
    onSubjectChange,
    onTextBeforeChange,
    onTextAfterChange,
    onPreviewDealershipNameChange,
    onEmailHtmlChange,
    onUseCustomHtmlChange,
    buildPreview,
    buildComposedHtml,
    subjectPlaceholder,
    textBeforePlaceholder,
    textAfterPlaceholder,
    dealershipTag,
    htmlEditorFooter,
}) => {
    const subjectRef = useRef<HTMLInputElement>(null);
    const textBeforeRef = useRef<HTMLTextAreaElement>(null);
    const textAfterRef = useRef<HTMLTextAreaElement>(null);
    const htmlRef = useRef<HTMLTextAreaElement>(null);
    const [htmlEditorOpen, setHtmlEditorOpen] = useState(false);

    const {previewSubject, previewHtml} = useMemo(
        () =>
            buildPreview({
                subject,
                textBefore,
                textAfter,
                previewDealershipName,
                emailHtml,
                useCustomHtml,
                htmlEditorOpen,
            }),
        [
            buildPreview,
            subject,
            textBefore,
            textAfter,
            previewDealershipName,
            emailHtml,
            useCustomHtml,
            htmlEditorOpen,
        ],
    );

    const switchPreviewToPlainFields = () => {
        if (useCustomHtml) {
            onUseCustomHtmlChange(false);
        }
    };

    const handleSubjectChange = (value: string) => {
        switchPreviewToPlainFields();
        onSubjectChange(value);
    };

    const handleTextBeforeChange = (value: string) => {
        switchPreviewToPlainFields();
        onTextBeforeChange(value);
    };

    const handleTextAfterChange = (value: string) => {
        switchPreviewToPlainFields();
        onTextAfterChange(value);
    };

    const {setValue: setSubjectValue, handleKeyDown: handleSubjectKeyDown} = useTextHistory(
        subject,
        handleSubjectChange,
    );
    const {setValue: setHtmlValue, handleKeyDown: handleHtmlKeyDown} = useTextHistory(emailHtml, onEmailHtmlChange);

    useEffect(() => {
        if (!htmlEditorOpen) return;
        if (useCustomHtml && emailHtml.trim()) {
            return;
        }
        setHtmlValue(buildComposedHtml(textBefore, textAfter));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [htmlEditorOpen]);

    const openHtmlEditor = () => {
        if (!useCustomHtml || !emailHtml.trim()) {
            setHtmlValue(buildComposedHtml(textBefore, textAfter));
            onUseCustomHtmlChange(false);
        }
        setHtmlEditorOpen(true);
        window.requestAnimationFrame(() => htmlRef.current?.focus());
    };

    const rebuildHtmlFromPlainFields = () => {
        const composed = buildComposedHtml(textBefore, textAfter);
        setHtmlValue(composed);
        onUseCustomHtmlChange(false);
    };

    const insertSubjectTag = () => {
        const el = subjectRef.current;
        if (!el) return;
        const {nextValue, cursor} = insertTextAtSelection(
            subject,
            dealershipTag,
            el.selectionStart ?? subject.length,
            el.selectionEnd ?? subject.length,
        );
        setSubjectValue(nextValue);
        window.requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(cursor, cursor);
        });
    };

    const richFieldProps = {
        minH: "72px",
        rows: 3,
        fontSize: "xs",
        size: "sm" as const,
        py: 2,
    };

    const previewPanel = (
        <Box w="100%">
            <Flex
                justify="space-between"
                align={{base: "flex-start", sm: "center"}}
                gap={2}
                mb={2}
                flexWrap="wrap"
            >
                <HStack spacing={2} flexWrap="wrap" flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        Preview
                    </Text>
                    <Input
                        flex={1}
                        minW={{base: "100%", sm: "240px"}}
                        maxW="100%"
                        size="md"
                        value={previewDealershipName}
                        onChange={(e) => onPreviewDealershipNameChange(e.target.value)}
                        placeholder="Sample Dealership LLC"
                        title="Dealership name for preview only"
                    />
                </HStack>
            </Flex>

            <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                bg="gray.50"
                overflow="hidden"
            >
                <Box px={3} py={1.5} bg="white" borderBottomWidth="1px" borderColor="gray.200">
                    <Text fontSize="2xs" color="gray.500" lineHeight="1.2" mb={0.5}>
                        Subject
                    </Text>
                    <Text fontSize="xs" fontWeight="medium" lineHeight="short">
                        {previewSubject || "—"}
                    </Text>
                </Box>
                {previewHtml ? (
                    <EmailPreviewShadow bodyHtml={previewHtml} minHeight={200} maxHeight="70vh" />
                ) : (
                    <VStack py={6} spacing={2} color="gray.400" bg="white">
                        <Text fontSize="xs">No content to preview</Text>
                    </VStack>
                )}
            </Box>
        </Box>
    );

    return (
        <SimpleGrid
            columns={{base: 1, lg: 2}}
            spacing={{base: 6, lg: 10}}
            w="100%"
            alignItems="start"
        >
            {previewPanel}
            <Box w="100%">
                <Stack spacing={2.5} maxH={{lg: "75vh"}} overflowY="auto" pr={1}>
                    <FormControl>
                        <FormLabel {...labelProps}>Email subject</FormLabel>
                        <HStack align="center" spacing={1.5}>
                            <Input
                                ref={subjectRef}
                                flex={1}
                                value={subject}
                                onChange={(e) => setSubjectValue(e.target.value)}
                                onKeyDown={handleSubjectKeyDown}
                                placeholder={subjectPlaceholder}
                                {...fieldProps}
                            />
                            <Button
                                size="xs"
                                variant="outline"
                                flexShrink={0}
                                whiteSpace="nowrap"
                                onClick={insertSubjectTag}
                            >
                                + dealer
                            </Button>
                        </HStack>
                        <FormHelperText {...helperProps}>
                            <Code fontSize="2xs">{dealershipTag}</Code> for dealership name; otherwise appended to
                            subject.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel {...labelProps}>Before product list</FormLabel>
                        <RichPlainTextarea
                            inputRef={textBeforeRef}
                            value={textBefore}
                            onChange={handleTextBeforeChange}
                            placeholder={textBeforePlaceholder}
                            dealershipTagButtonLabel="+ dealer"
                            {...richFieldProps}
                        />
                        <FormHelperText {...helperProps}>
                            B / I or {dealershipTag}. Blank line = paragraph gap.
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel {...labelProps}>After product list</FormLabel>
                        <RichPlainTextarea
                            inputRef={textAfterRef}
                            value={textAfter}
                            onChange={handleTextAfterChange}
                            placeholder={textAfterPlaceholder}
                            dealershipTagButtonLabel="+ dealer"
                            {...richFieldProps}
                        />
                        <FormHelperText {...helperProps}>Company footer added automatically.</FormHelperText>
                    </FormControl>

                    <FormControl>
                        <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
                            <FormLabel {...labelProps} mb={0}>
                                HTML template
                                {!htmlEditorOpen && (
                                    <Text as="span" fontWeight="normal" color="gray.500" ml={1}>
                                        ({useCustomHtml ? "custom" : "from fields"})
                                    </Text>
                                )}
                            </FormLabel>
                            <HStack spacing={1}>
                                {htmlEditorOpen && (
                                    <Button size="xs" variant="outline" onClick={rebuildHtmlFromPlainFields}>
                                        Rebuild
                                    </Button>
                                )}
                                <Button
                                    size="xs"
                                    variant={htmlEditorOpen ? "solid" : "outline"}
                                    colorScheme={htmlEditorOpen ? "blue" : undefined}
                                    onClick={() =>
                                        htmlEditorOpen ? setHtmlEditorOpen(false) : openHtmlEditor()
                                    }
                                >
                                    {htmlEditorOpen ? "Hide" : "Edit HTML"}
                                </Button>
                            </HStack>
                        </Flex>
                        <Collapse in={htmlEditorOpen} animateOpacity unmountOnExit>
                            <Box pt={1.5}>
                                <Textarea
                                    ref={htmlRef}
                                    value={emailHtml}
                                    onChange={(e) => setHtmlValue(e.target.value)}
                                    onKeyDown={handleHtmlKeyDown}
                                    minH="160px"
                                    rows={8}
                                    fontFamily="mono"
                                    fontSize="2xs"
                                    size="sm"
                                    py={2}
                                />
                                {htmlEditorFooter && (
                                    <FormHelperText {...helperProps}>{htmlEditorFooter}</FormHelperText>
                                )}
                            </Box>
                        </Collapse>
                    </FormControl>
                </Stack>
            </Box>
        </SimpleGrid>
    );
};

export default MailTemplateEditor;
